import {AnalyticsChartCategory} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-data.interface";

export const DataCollectType = {
  Category: "category",
  Numeric: "numeric"
}
//======================================================================================
//======================================================================================

export interface IAnalyticsChartsDataCollector {
  addValue: (value: any) => void;
  setCount: (label: any, value: number) => void;
  getCategories: () => Array<AnalyticsChartCategory>;
  resetCounters: () => void;
}
//======================================================================================
//======================================================================================

export interface IAnalyticsChartsDataCollectorBuilder<T extends IAnalyticsChartsDataCollector> {
  build: () => T;
}
//======================================================================================
//======================================================================================

export class AnalyticsChartsCategoryDataCollector implements IAnalyticsChartsDataCollector {

  // input values
  groups: any;
  initByFixGroups: boolean; // indicate if groups were initialized using a fix list
  numGroups: number;

  // hold values that exceed the given min/max range
  private othersGroup: any;

  //------------------------------------------------------------------------------------

  /**
   * set the categories if they are known in advance (method is invoked internally)
   */
  setGroups(categories: string[]): AnalyticsChartsCategoryDataCollector {

    // loop all given categories' keys
    for (let i = 0; i < categories.length; i++) {
      let key = categories[i];
      this.groups[key] = this.createObject(key, false);
    }

    return this;
  }
  //------------------------------------------------------------------------------------

  addValue (value: any) {
    let group: any = this.getGroup(value);

    // increment the counter of this group
    group.count++;

  }
  //------------------------------------------------------------------------------------

  setCount(label: any, value: number) {

    let group: any = this.getGroup(label);
    if (group.isOthers) {
      group.count += value;
    } else {
      group.count = value;
    }
  }
  //------------------------------------------------------------------------------------

  getGroup(label: any): any {

    // get the group for the given key
    let group: any = this.groups[label];

    // if it is indicated that groups were initialized using a fixed list:
    if (this.initByFixGroups) {

      // if group was not initially defined, add value to 'Others' category
      if (! group) {
        if (! this.othersGroup) {
          this.othersGroup = this.createObject('others', true);
        }
        group = this.othersGroup;

      }

    // categories were not defined in advance:
    } else if (! group) { // if group was not created yet, create it
      group = this.createObject(label, false);
      this.groups[label] = group;

    }

    return group;
  }
  //------------------------------------------------------------------------------------

  createObject(label: any, isOthers: boolean) {

    return {
      label: label,
      count:0,
      isOthers: isOthers
    };

  }
  //------------------------------------------------------------------------------------

  getCategories(): Array<AnalyticsChartCategory> {

    // build output categories
    let categories = new Array<AnalyticsChartCategory>();

    // if number of group exceeds the maximum number, add all small categories to the
    //  'Others' category
    let keys = Object.keys(this.groups);
    let numToOthers = keys.length - this.numGroups;

    if (numToOthers > 0) {

      // sort groups from smaller to greater
      keys.sort((k1, k2) => {
        if (this.groups[k1].count > this.groups[k2].count) return 1;
        if (this.groups[k1].count < this.groups[k2].count) return -1;
        return 0;
      });

      // add all smallest groups to 'Others'
      if (!this.othersGroup) {
        this.othersGroup = this.createObject('others', true);
      }

    }

    keys.forEach(key => {

      // if it is required to add values to 'Others':
      if (numToOthers > 0) {
        this.othersGroup.count += this.groups[key].count;
        numToOthers--;
      } else {
        categories.push(new AnalyticsChartCategory(key, this.groups[key].count));
      }

    });

    // if 'others' group is defined, add it to the output array
    if (this.othersGroup) {
      categories.push(new AnalyticsChartCategory('Others', this.othersGroup.count));
    }

    return categories;
  }
  //------------------------------------------------------------------------------------

  resetCounters() {

    if (this.groups) {
      Object.keys(this.groups).forEach(key => this.groups[key].count = 0);
    }

    if (this.othersGroup) {
      this.othersGroup.count = 0;
    }

  }
  //------------------------------------------------------------------------------------

};
//======================================================================================
//======================================================================================

/**
 * this class constructs AnalyticsChartsCategoryDataCollector instance
 */
export class AnalyticsChartsCategoryCollectorBuilder implements IAnalyticsChartsDataCollectorBuilder<AnalyticsChartsCategoryDataCollector> {

  // input values
  private numGroups: number = 7; // default number of groups
  private categories: string[];
  //------------------------------------------------------------------------------------

  /**
   * Option 1: set the categories, if to override current values
   */
  withGroups(categories: string[]): AnalyticsChartsCategoryCollectorBuilder {
    this.categories = categories;

    return this;
  }
  //------------------------------------------------------------------------------------

  /**
   * Option 2: set number of categories
   *
   * NOTE: (*) this method is relevant only if categories are NOT known in advance (Option 1)
   *       (*) if value is unknown in advance, default value will be used
   */
  withNumGroups(numGroups: number): AnalyticsChartsCategoryCollectorBuilder {
    this.numGroups = numGroups;

    return this;
  }
  //------------------------------------------------------------------------------------

  /**
   * construct & return AnalyticsChartsCategoryDataCollector instance
   */
  build(): AnalyticsChartsCategoryDataCollector {

    let instance: AnalyticsChartsCategoryDataCollector;

    // fixed categories are provided:
    if (this.categories) {
      this.numGroups = this.categories.length;

      // construct instance
      instance = this.constructInstance(true);

      instance.setGroups(this.categories);

    // no input was provided
    } else {

      // construct instance
      instance = this.constructInstance(false);

    }

    // return instance
    return instance;
  }
  //------------------------------------------------------------------------------------

  private constructInstance(initByFixGroups: boolean): AnalyticsChartsCategoryDataCollector {

    let instance: AnalyticsChartsCategoryDataCollector = new AnalyticsChartsCategoryDataCollector();

    instance.groups = [];
    instance.initByFixGroups = initByFixGroups;
    instance.numGroups = this.numGroups;

    return instance;
  }
  //------------------------------------------------------------------------------------


};
//======================================================================================
//======================================================================================

/**
 * this class is constructed by AnalyticsChartsNumberGroupBuilder
 */
export class AnalyticsChartsNumberDataCollector implements IAnalyticsChartsDataCollector {

  // input values
  groups: Array<any>;
  initByFixGroups: boolean; // indicate if groups were initialized using a fix list
  hasDecimalValue: boolean = false; // indicate if there are any decimal values
  minRange: number;
  maxRange: number;
  numGroups: number;
  incrementSize: number;

  // number of items in each group
  private groupSize: number;

  // Note: if range is not provided, all values will first added to the 'tempContainer', and the actual groups will
  //       be calculated later when groups are retrieved
  private tempContainer: any;

  // hold values that exceed the given min/max range
  private othersGroup: any;

  // hold NaN values
  private NaNGroup: any;
  //------------------------------------------------------------------------------------

  /**
   * set the categories if they are known in advance (method is invoked internally)
   */
  setGroups(categories: number[]): AnalyticsChartsNumberDataCollector {

    // loop all given bounds
    for (let i = 0; i < categories.length; i++) {

      let minBound: number = categories[i];
      let maxBound: number = categories[i+1];

      this.groups.push(this.createObject(minBound, maxBound));

      i++; // move next bound
    }

    return this;
  }
  //------------------------------------------------------------------------------------

  initNumGroups(): number {

    let numGroupsSet = !isNaN(this.numGroups);
    let incrementSizeSet = !isNaN(this.incrementSize);
    let rangeSize = (this.maxRange - this.minRange);
    let isDecimalIncrement =  this.hasDecimalValue ||
                              (incrementSizeSet && ! Number.isInteger(this.incrementSize));

    // ensure that the number of groups will be proportional to the current range. This is
    //  to avoid more groups than needed
    let groupsInRangeMinRatio = 0.5;
    let numGroups = numGroupsSet ? this.numGroups : 10;

    let incFactor = incrementSizeSet ?
                      this.incrementSize :
                      this.getDefIncSize(isDecimalIncrement, false); // just a first initialization

    if (! numGroupsSet && ((numGroups / (rangeSize*incFactor)) > groupsInRangeMinRatio)) {
      numGroups = Math.ceil(rangeSize*groupsInRangeMinRatio);
    }

    // calculate the number of elements on each group
    this.groupSize = (rangeSize / numGroups);

    let isGroupSizeDecimal = ! Number.isInteger(this.groupSize);

    // if the group's size is greater than 2 OR groupSize is decimal BUT no decimal value -->
    //  use fixed group's size
    if (this.groupSize > 2 ||
      (isGroupSizeDecimal && !isDecimalIncrement)) {

      this.groupSize = Math.round(this.groupSize);
      isGroupSizeDecimal = false;
    }

    // if incrementSize is not provided or not calculated, use default
    if (numGroups == 1) {
      this.incrementSize = 0;

    } else if (!incrementSizeSet ||
      (this.incrementSize >= this.groupSize)) {

      this.incrementSize = this.getDefIncSize(isDecimalIncrement, isGroupSizeDecimal);

      // need to invoke again since that the calculations of the factors may be changed
      if (this.incrementSize != incFactor) {
        return this.initNumGroups();
      }

    }

    return numGroups;
  }
  //------------------------------------------------------------------------------------

  getDefIncSize(isDecimalIncrement, isGroupSizeDecimal): number {

    if (isDecimalIncrement || isGroupSizeDecimal) {
      return 0.01;
    } else {
      return 1;
    }

  }
  //------------------------------------------------------------------------------------

  /**
   * build the categories based on the provided min/max range & number of categories  (method is invoked internally)
   */
  calculateGroups(): AnalyticsChartsNumberDataCollector {

    // calculate the number of groups to set
    this.numGroups = this.initNumGroups();

    // build the groups
    let i = 0;
    let minBound;
    let maxBound;
    while (i >= 0) {

      // calculate the lower/upper bounds for this group
      if (maxBound) {
        minBound = maxBound + this.incrementSize;
      } else {
        minBound = this.minRange + (this.groupSize * i);
      }
      maxBound = minBound + this.groupSize - this.incrementSize;

      // (1) if the upper bound of this groups EQUALS to the max-range, it is required to add
      //     another group with lower-bound equals the max-range. This is because values are
      //     added to groups if >= lower-bound
      //
      // (2) if exceeding max-value, force the max-value and don't add further groups
      //     this situation can be derived when using fixed group's size
      if (minBound > this.maxRange) break;

      // add a new category
      this.groups.push(this.createObject(minBound, maxBound));

      if (maxBound >= this.maxRange) break;

      i++;

    }

    return this;
  }
  //------------------------------------------------------------------------------------

  createObject(minBound, maxBound) {

    return {
      minBound: minBound,
      maxBound: maxBound,
      count:0
    };
  }
  //------------------------------------------------------------------------------------

  addToValuesGroup(value: number, group: any): any {

    if (! group) {
      group = this.createObject(value, value);
    }

    // set the min/max value of the othersGroup so it will reflect the range of values of all items added to it
    group.minBound = Math.min(value, group.minBound);
    group.maxBound = Math.max(value, group.maxBound);

    // increment the counter of this group
    group.count++;

    return group;
  }
  //------------------------------------------------------------------------------------

  addValue (value: any) {

    let nanGroup = this.getGroupIfNaN(value);
    if (nanGroup) {
      nanGroup.count++; // increment the counter of this group
      return;
    }

    // indicate if there are any decimal values
    if (! this.hasDecimalValue) {
      this.hasDecimalValue = !Number.isInteger(value);
    }

    // if groups array was not initialized yet - add all values to a temporary array
    if (! this.groups) {

      this.tempContainer = this.addToValuesGroup(value, this.tempContainer);

      // add value
      if (! this.tempContainer.values) {
        this.tempContainer.values = [];
      }
      this.tempContainer.values.push(value);

      return;
    }

    // if value exceeds defined range, add it to 'others' group
    if (value < this.minRange || value > this.maxRange) {
      this.othersGroup = this.addToValuesGroup(value, this.othersGroup);
      return;
    }

    // if it is indicated that groups were initialized using a fixed list, it is required to search for the relevant
    //  group in order to place the value
    if (this.initByFixGroups) {

      // loop from last element to the first one
      for (let i = this.groups.length-1; i >= 0; i--) {

        // if value >= current group's min-bound, add the value to this group
        if (value >= this.groups[i].minBound) {
          this.groups[i].count++; // increment the counter of this group and exit
          return;
        }

      }

    }

    // groups were initialized by specific range:

    // calculate the group number that this value relates to
    let groupNum: any = (value - this.minRange) / this.groupSize;
        groupNum = parseInt(groupNum);

    // increment the counter of this group
    this.groups[groupNum].count++;

  }
  //------------------------------------------------------------------------------------

  setCount(label: any, value: number) {

    if (this.groups && this.groups[label]) {
      this.groups[label].count = value;
      return;
    }

    let nanGroup = this.getGroupIfNaN(value);
    if (nanGroup) {
      nanGroup.count = value;
      return;
    }

  }
  //------------------------------------------------------------------------------------

  getGroupIfNaN(value: any): any {

    if (isNaN(value)) {
      if (!this.NaNGroup) {
        this.NaNGroup = this.createObject(NaN, NaN);
      }

      return this.NaNGroup;
    }

  }
  //------------------------------------------------------------------------------------

  getCategories(): Array<AnalyticsChartCategory> {
    
    // if groups array was not initialized before BUT values were added to othersGroup - calculate the groups by the
    //  values added so far
    if (! this.groups) {
      if (! this.tempContainer) return [];

      // set min/max range values, as were added to the tempContainer
      this.groups = [];
      this.minRange = this.tempContainer.minBound;
      this.maxRange = this.tempContainer.maxBound;

      // build the groups
      this.calculateGroups();

      // re-arrange all values in their related group
      this.tempContainer.values.forEach(value => this.addValue(value));

      this.tempContainer = undefined; // clear temporary container
    }

    // build output categories
    let categories = new Array<AnalyticsChartCategory>();

    // category's label is as the group's min-value
    this.groups.forEach(group => {

      let gLabel;

      // if minBound equals to maxBound, do not set a range
      if (group.minBound == group.maxBound) {
          gLabel = ''+group.minBound;
      } else {
          gLabel = (Math.trunc(group.minBound * 100) / 100) + '-' +
                   (Math.trunc(group.maxBound * 100) / 100);
      }

      // create a new category and mark to compare categories by their 'minBound' value
      //  (and not by the 'label' as a string)
      this.createAndAddCategory(categories, gLabel, group.count, group.minBound);
    });

    // if 'others' group is defined, add it to the output array
    // Note: when sorting categories, 'others' group should come after regular groups and before 'NA' group
    if (this.othersGroup) {
      this.createAndAddCategory(categories, 'Others', this.othersGroup.count, Number.MAX_VALUE-1);
    }

    // if 'NaN' group is defined, add it to the output array
    if (this.NaNGroup) {
      this.createAndAddCategory(categories, 'NA', this.NaNGroup.count, Number.MAX_VALUE);
    }

    return categories;
  }
  //------------------------------------------------------------------------------------

  createAndAddCategory(categories: Array<AnalyticsChartCategory>, label: string, count: number, compareBy: number) {

    let category = new AnalyticsChartCategory(label, count);
    category.compareBy = compareBy;

    categories.push(category);
  }
  //------------------------------------------------------------------------------------

  resetCounters() {

    if (this.groups) {
      this.groups.forEach(group => group.count = 0);
    }

    if (this.othersGroup) {
      this.othersGroup.count = 0;
    }

    if (this.NaNGroup) {
      this.NaNGroup.count = 0;
    }

    this.tempContainer = undefined; // clear temporary container
  }
  //------------------------------------------------------------------------------------

}
//======================================================================================
//======================================================================================

/**
 * this class constructs AnalyticsChartsNumberDataCollector instance
 */
export class AnalyticsChartsNumberCollectorBuilder implements IAnalyticsChartsDataCollectorBuilder<AnalyticsChartsNumberDataCollector> {

  // set defaults for in case that values are not provided
  //  (*) min value as MAXIMUM possible number
  //  (*) max value as MINIMUM possible number
  //  (*) number of groups = 10
  private minRange: number = Number.MAX_VALUE;
  private maxRange: number = Number.MIN_VALUE;
  private numGroups: number = NaN;
  private incrementSize: number = NaN;
  private categories: number[];

  private hasRange: boolean = false;
  //------------------------------------------------------------------------------------

  /**
   * Option 1: set the categories if they are known in advance
   */
  withGroups(categories: number[]): AnalyticsChartsNumberCollectorBuilder {
    this.categories = categories;

    return this;
  }
  //------------------------------------------------------------------------------------

  /**
   * Option 2: set range of values by which groups will be defined
   *
   * NOTE: (*) if range is unknown in advance, it will be concluded by the actual value items
   */
  withRange(minRange: number, maxRange: number): AnalyticsChartsNumberCollectorBuilder {
    this.minRange = minRange;
    this.maxRange = maxRange;
    this.hasRange = true;

    return this;
  }
  //------------------------------------------------------------------------------------

  /**
   * Option 2: set size by which categories will be incremented
   *
   * NOTE: (*) if incrementSize is not provided, it will be concluded by the actual value items
   */
  withIncrementSize(incrementSize: number): AnalyticsChartsNumberCollectorBuilder {
    this.incrementSize = incrementSize;

    return this;
  }
  //------------------------------------------------------------------------------------

  /**
   * Option 2: set number of categories
   *
   * NOTE: (*) this method is relevant only if categories are NOT known in advance (Option 1)
   *       (*) if value is unknown in advance, default value will be used
   */
  withNumGroups(numGroups: number): AnalyticsChartsNumberCollectorBuilder {
    this.numGroups = numGroups;

    return this;
  }
  //------------------------------------------------------------------------------------

  /**
   * construct & return AnalyticsChartsNumberDataCollector instance
   */
  build(): AnalyticsChartsNumberDataCollector {

    let instance: AnalyticsChartsNumberDataCollector;

    // fixed categories are provided:
    if (this.categories) {
      this.numGroups = this.categories.length;

      // min value must be as the first provided bound
      this.minRange = this.categories[0];

      // max value must be as the last provided bound
      this.maxRange = this.categories[this.categories.length-1];

      // construct instance
      instance = this.constructInstance([], true);

      // set the categories
      instance.setGroups(this.categories);

    // fixed range was provided:
    } else if (this.hasRange) {

      // construct instance
      instance = this.constructInstance([], false);

      // build the categories based on the provided min/max range & number of categories
      instance.calculateGroups();

    // no input was provided
    } else {

      // construct instance
      instance = this.constructInstance(undefined, false);

    }

    // return instance
    return instance;
  }
  //------------------------------------------------------------------------------------

  private constructInstance(groups: Array<any>, initByFixGroups: boolean): AnalyticsChartsNumberDataCollector {

    let instance: AnalyticsChartsNumberDataCollector = new AnalyticsChartsNumberDataCollector();

    instance.groups = groups;
    instance.initByFixGroups = initByFixGroups;
    instance.minRange = this.minRange;
    instance.maxRange = this.maxRange;
    instance.numGroups = this.numGroups;
    instance.incrementSize = this.incrementSize;

    return instance;
  }
}
//======================================================================================
//======================================================================================
